import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth";
import "./Login.css";
import oxfordLogo from "../media/oxford-logo-long.svg";
import ToolTip from "../components/component-tools/ToolTip";

interface LoginProps {
  handleLoginButtonClick: (portal: string) => void;
}

const Login: React.FC<LoginProps> = ({ handleLoginButtonClick }) => {
  const [email, setEmail] = useState("");
  const [portal, setPortal] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const { loginWithMsal } = useAuth();
  const { loginWithAuth0 } = useAuth();
  const location = useLocation();
  const successMessage = location.state?.successMessage;
  const failMessage = location.state?.failMessage;
  const [loadingSpinner, setLoadingSpinner] = useState(false);
  const navigate = useNavigate();

  const PROD_APP_URL = process.env.REACT_APP_PROD_APP_URL || "";
  const API_URL = process.env.REACT_APP_API_URL || "";
  const DEV_API_URL = process.env.REACT_APP_DEV_API_URL || "";
  const PROD_API_URL = process.env.REACT_APP_PROD_API_URL || "";

  const apiBaseUrl =
    window.location.hostname === "localhost"
      ? API_URL
      : window.location.hostname === new URL(PROD_APP_URL).hostname
        ? PROD_API_URL
        : DEV_API_URL;

  const loginInFlightRef = React.useRef(false);

  useEffect(() => {
    const storedEmail = localStorage.getItem("email");
    const storedPortal = localStorage.getItem("portalType");

    const searchParams = new URLSearchParams(location.search);
    const portalFromUrl = searchParams.get("portal");
    const emailFromUrl = searchParams.get("email");

    if (emailFromUrl) {
      setEmail(emailFromUrl);
    } else if (storedEmail) {
      setEmail(storedEmail);
    }

    if (portalFromUrl === "client" || portalFromUrl === "candidate") {
      setPortal(portalFromUrl);
    } else if (storedPortal) {
      setPortal(storedPortal);
    }
  }, [location.search]);

  const handleLogin = async () => {
    if (loginInFlightRef.current) return;

    if (!email) {
      setErrorMessage("Please enter your email.");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setErrorMessage("Please enter a valid email address.");
      return;
    }

    if (!portal) {
      setErrorMessage("Please select a portal.");
      return;
    }

    loginInFlightRef.current = true;

    try {
      setLoadingSpinner(true);
      const response = await fetch(`${apiBaseUrl}/api/auth/verify-email`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, portal }),
      });

      const data = await response.json();
      if (!response.ok) {
        setErrorMessage(
          `Email verification failed, or email not found in portal: ${data.message}`,
        );
        return;
      }

      let token: string | null = null;
      let authError = "";

      if (portal === "admin") {
        [token, authError] = await loginWithMsal(email, portal);
      } else {
        [token, authError] = await loginWithAuth0(email, portal);
      }

      if (!token && authError?.toLowerCase().includes("redirecting")) {
        return;
      }

      if (!token) {
        setErrorMessage(`Login failed. Please try again: ${authError}`);
        return;
      }

      localStorage.setItem("token", token);
      localStorage.setItem("portalType", portal);

      window.location.href = `/${portal}`;
    } catch (error) {
      setErrorMessage(`An error occurred. Please try again: ${error}`);
    } finally {
      setLoadingSpinner(false);
      loginInFlightRef.current = false;
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleLogin();
    }
  };

  return (
    <div
      className="content-container-login"
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      <div className="login-container">
        <div className="mobile-warning">
          For the best experience, we recommend using a computer to use this
          application.
        </div>
        <div className="logo-help-wrapper">
          <img className="oxford-logo-long" src={oxfordLogo} alt="Logo" />
          <button
            className="help-icon-button"
            onClick={() => navigate("/help")}
            aria-label="Help"
          >
            <i className="bi bi-question-circle"></i>
          </button>
        </div>
        <h2>Login to Your Existing Account </h2>

        <div className="portal-selection">
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "5px",
              height: "30px",
            }}
          >
            <input
              type="radio"
              value="client"
              checked={portal === "client"}
              onChange={() => setPortal("client")}
            />{" "}
            <span>Client</span>
            <ToolTip
              tooltipText="The organization requesting background checks."
              placement="right"
              className="info-icon-spec"
            ></ToolTip>
          </label>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "5px",
              height: "30px",
            }}
          >
            <input
              type="radio"
              value="candidate"
              checked={portal === "candidate"}
              onChange={() => setPortal("candidate")}
            />{" "}
            <span>Candidate</span>
            <ToolTip
              tooltipText="The individual being screened for the organization."
              placement="right"
              className="info-icon-spec"
            ></ToolTip>
          </label>
        </div>

        <div className="email-field">
          <label>Email:</label>

          <input
            className="form-control form-control-md"
            type="email"
            value={email}
            placeholder="Enter your email"
            onChange={(e) => setEmail(e.target.value)}
          ></input>
        </div>

        {/* <div className="pop-up-warning">
          ⚠️ Application maintenance is in progress.  You can continue to use
          the application but may experience slower performance during this
          time.
        </div> */}

        {/* Comment out one or the other */}
        {/*<div className="pop-up-warning">
          ⚠️ When logging in, please disable pop-up blockers — identity
          verification will open in a new window.
        </div> */}

        {/* <div className="pop-up-error">
          🚫 We’re performing a scheduled update. Please try again later.
        </div> */}

        <button
          onClick={handleLogin}
          type="button"
          disabled={loadingSpinner}
          className={`oxbtn ${
            portal !== "" && email !== "" ? "btn-purple" : "btn-login"
          }`}
        >
          {loadingSpinner ? "Logging in..." : "Login"}
        </button>
        <div>
          {successMessage && (
            <div className=" alert alert-success fade show">
              {successMessage}
            </div>
          )}
          {failMessage && (
            <div className=" alert alert-danger fade show">{failMessage}</div>
          )}
        </div>
        <div>
          <a href="/registration">New here? Click here to register!</a>
        </div>
        <div className="text-container-login" style={{ textAlign: "center" }}>
          {" "}
          <a href="/admin-login">Admin Login</a>
        </div>
      </div>

      {errorMessage && (
        <div
          className="alert-message alert alert-danger alert-dismissible fade show"
          role="alert"
        >
          {errorMessage}
          <button
            type="button"
            className="btn-close"
            onClick={() => setErrorMessage("")}
            aria-label="Close"
          ></button>
        </div>
      )}
      {loadingSpinner && (
        <div className="loading-mask">
          <div className="loading-spinner"></div>
        </div>
      )}
    </div>
  );
};

export default Login;
